import React, { useEffect, useMemo } from 'react';

import GaranIcon from 'assets/icons/Garan';
import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import TextButton from 'components/text/TextButton';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { injectWysiwygTokens } from 'utils/wysiwygTokens';
import styled from 'libs/styled';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import zIndex from 'config/theme/z-index';

// Tag used for the {{TOKEN}} pseudo-tags injected into the wysiwyg data
const tokenTag = 'harmonisationToken';
const HARMONISATION_TRIGGER = {
    icon: 'icon',
    text: 'text',
};

const Wrapper = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    text-wrap: pretty;
`;

const IconButton = styled('button')`
    display: inline-flex;

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const showHarmonisationOverlay = (overlay, returnFocusId) =>
    overlay.show('harmonisationOverlay', {
        transparent: false,
        zIndex: zIndex.basket,
        returnFocusId,
    });

// Renders the text inside a {{TOKEN}} as a button that opens the harmonisation overlay
const HarmonisationTrigger = ({ children }) => {
    const overlay = useSelector(state => state.overlay);

    return (
        <TextButton
            aria-haspopup="dialog"
            data-harmonisation-trigger={HARMONISATION_TRIGGER.text}
            type="button"
            onClick={() => showHarmonisationOverlay(overlay, HARMONISATION_TRIGGER.text)}
        >
            {children}
        </TextButton>
    );
};

HarmonisationTrigger.propTypes = {
    children: PropTypes.node.isRequired,
};

const HarmonisationSection = ({ ...rest }) => {
    const { t } = useTranslation();
    const overlay = useSelector(state => state.overlay);
    const returnFocusId = overlay.data?.returnFocusId;
    const harmonisationText = useSelector(state => state.header.data.basket?.harmonisationText);

    // Turns every {{TOKEN}} in the text into a tag that HarmonisationTrigger renders
    const data = useMemo(() => injectWysiwygTokens(harmonisationText, tokenTag), [harmonisationText]);

    useEffect(() => {
        if (!returnFocusId) {
            return;
        }

        const trigger = document.querySelector(`[data-harmonisation-trigger="${returnFocusId}"]`);
        trigger?.focus();
    }, [returnFocusId]);

    if (!harmonisationText?.length) {
        return null;
    }

    return (
        <Wrapper {...rest}>
            <IconButton
                aria-haspopup="dialog"
                aria-label={t('checkout_basket.harmonisation')}
                data-harmonisation-trigger={HARMONISATION_TRIGGER.icon}
                type="button"
                onClick={() => showHarmonisationOverlay(overlay, HARMONISATION_TRIGGER.icon)}
            >
                <GaranIcon aria-hidden="true" />
            </IconButton>
            <Wysiwyg
                data={data}
                textComponent={props => <Paragraph {...props} />}
                validTags={['url', 'i', 'b', 'u', tokenTag]}
                tagComponents={{ url: TextLinkWysisyg, [tokenTag]: HarmonisationTrigger }}
            />
        </Wrapper>
    );
};

export default HarmonisationSection;
